import { useCallback, useEffect, useState } from 'react';
import {
  goToQuestionWidget,
  goToPassageWidget,
  getPassageFromItem,
} from 'legacy/src/components/learnosity/utils/helpers';
import suppressLearnosityAlerts from 'legacy/src/components/learnosity/LearnosityAlertHelper';

import { validationActionTypes } from '../constants';
import { omit } from 'lodash';

const getValidationKeys = (validations) =>
  Object.keys(validations)
    .filter((key) => key.startsWith('has'))
    .map((key) => validations[key]);

export default function usePublishEffects({
  router,
  query = {},
  question,
  questionTags,
  authoringApiInstance,
  passageApiInstance,
  masterApiInstance,
  setTags,
  allRequiredAreSelected,
  isTagSelected,
  setTitle,
  publish,
  validations,
  dispatchValidation,
  setSelectedTab,
  isEditing,
  appendCopyToTitle = false,
  copyQuestion = false,
  replaceQuestion = false,
  savedQuestion = null,
  isFrq = false,
  isDisabledTitle = false,
  setIsDisabledTitle = () => {},
  handleTitleFocus = () => {},
  isAuthoringNewPage,
  authoringPageActions,
}) {
  let removeLeaveHook = () => {};

  const [passageItem, setPassageItem] = useState(null);

  const hasSomethingFilled = useCallback(
    () => getValidationKeys(validations).some(Boolean) && !publish.published,
    [validations, publish.published],
  );

  const onBeforeUnload = (e) => {
    const isLogout = router.location.pathname === '/logout';
    if (hasSomethingFilled() && !isLogout) {
      e.preventDefault();
      e.returnValue = 'value';
    } else {
      delete e.returnValue;
    }
  };

  const handleNavigationLeave = () => {
    if (hasSomethingFilled()) {
      return 'Are you sure want to leave? You have unsaved changes';
    }

    if (!authoringApiInstance) {
      suppressLearnosityAlerts();
    }

    return true;
  };

  const questionTitle = question ? question.title : '';

  // confirm when leaving to other websites e.g. amerit-nuevo (except on logout)
  useEffect(() => {
    // confirm when leaving to other websites e.g. amerit-nuevo (except on logout)
    window.addEventListener('beforeunload', onBeforeUnload);

    removeLeaveHook = router?.setRouteLeaveHook(
      router.routes[router.routes.length - 1],
      handleNavigationLeave,
    );

    return () => {
      // don't affect navigation that happen on other pages e.g. Scoring Guide downloads
      window.removeEventListener('beforeunload', onBeforeUnload);
      removeLeaveHook?.();
    };
  }, [hasSomethingFilled]);

  useEffect(() => {
    const title =
      questionTitle && appendCopyToTitle
        ? `${questionTitle} (Copy)`
        : questionTitle;
    setTitle(title);
    dispatchValidation({ type: validationActionTypes.hasName, payload: title });
  }, [questionTitle]);

  useEffect(() => {
    setTags(questionTags);
    dispatchValidation({
      type: validationActionTypes.hasAllRequiredTagsSelected,
      payload: allRequiredAreSelected,
    });
  }, [questionTags]);

  useEffect(() => {
    dispatchValidation({
      type: validationActionTypes.hasTagSelected,
      payload: isTagSelected,
    });
  }, [isTagSelected]);

  useEffect(
    () =>
      dispatchValidation({
        type: validationActionTypes.hasAllRequiredTagsSelected,
        payload: allRequiredAreSelected,
      }),
    [allRequiredAreSelected],
  );

  useEffect(() => {
    return () => {
      // don't affect navigation that happen on other pages e.g. Scoring Guide downloads
      window.removeEventListener('beforeunload', onBeforeUnload);
      removeLeaveHook?.();
      setSelectedTab(null);
    };
  }, []);

  useEffect(() => {
    const publishConditions =
      !publish.publishing &&
      publish.published &&
      publish.navigateToAfterPublishing &&
      !isAuthoringNewPage;

    if (
      publishConditions &&
      (copyQuestion || replaceQuestion) &&
      savedQuestion
    ) {
      const updatedQuery = {
        ...omit(query, ['returnPath', 'copyQuestion', 'replaceQuestion']),
        newQuestionItemId: savedQuestion.item_id,
      };
      router.push({
        pathname: publish.navigateToAfterPublishing,
        query: updatedQuery,
      });
    }

    if (publishConditions && !copyQuestion && !replaceQuestion) {
      router.push(publish.navigateToAfterPublishing);
    }

    // When a new question is pusblished
    // don't go anywhere, just reload the parent component
    const authoredNewConditions =
      !publish.publishing && publish.published && isAuthoringNewPage;

    if (authoredNewConditions) {
      authoringPageActions.reload();
    }
  }, [
    publish.publishing,
    publish.published,
    publish.navigateToAfterPublishing,
    savedQuestion,
  ]);

  useEffect(() => {
    // on edit item navigate Learnosity itemto MCQ widget or passage if present
    if (authoringApiInstance && isEditing) {
      const onItemRender = () => {
        // extract passage info from 'main' instance
        setPassageItem(getPassageFromItem(authoringApiInstance.getItem()));
        goToQuestionWidget(authoringApiInstance);
      };

      authoringApiInstance.on('render:item', onItemRender);
      return () => authoringApiInstance.off('render:item', onItemRender);
    }
  }, [authoringApiInstance, isEditing]);

  useEffect(() => {
    if (passageApiInstance && isEditing) {
      const onItemRender = () => {
        goToPassageWidget(passageApiInstance);
      };
      passageApiInstance.on('render:item', onItemRender);
      return () => passageApiInstance.off('render:item', onItemRender);
    }
  }, [passageApiInstance, isEditing]);

  useEffect(() => {
    if (masterApiInstance) {
      const onItemRender = () => {
        // delete all widgets on master item to have a clean state for saving,
        // we do it with DOM as Learnosity does not provide any API for removing widgets
        const deleteButtons = Array.from(
          document.getElementsByClassName(
            'lrn-author-widget-sidebar-delete-btn',
          ),
        );

        // simulate click with confirm
        deleteButtons.forEach((button) => button.click());
        setTimeout(() => {
          deleteButtons.forEach((button) => button.click());
        }, 500);
        // do it only once, other events do not fit
        masterApiInstance.off('render:item', onItemRender);
      };

      masterApiInstance.on('render:item', onItemRender);
    }
  }, [masterApiInstance]);

  useEffect(() => {
    if (publish.publishing || (!isFrq && !authoringApiInstance)) {
      setIsDisabledTitle(true);
    } else {
      setIsDisabledTitle(false);
    }
  }, [publish.publishing, authoringApiInstance]);

  useEffect(() => {
    if (isDisabledTitle === false) {
      handleTitleFocus();
    }
  }, [isDisabledTitle]);

  return [passageItem];
}
